import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import API_BASE from '../../utils/api';

const PROVIDERS = [
  { value: '', label: '— Select Provider —' },
  { value: 'msg91', label: 'MSG91', desc: 'Popular Indian SMS gateway' },
  { value: 'fast2sms', label: 'Fast2SMS', desc: 'Cost-effective bulk SMS for India' },
  { value: 'twilio', label: 'Twilio', desc: 'International SMS with wide coverage' },
  { value: 'textlocal', label: 'Textlocal', desc: 'UK-based global SMS provider' },
];

const PROVIDER_FIELDS = {
  msg91:     ['smsApiKey', 'smsSenderId'],
  fast2sms:  ['smsApiKey'],
  twilio:    ['smsApiKey', 'smsAuthToken', 'smsFromNumber'],
  textlocal: ['smsApiKey', 'smsSenderId'],
};

const FIELD_LABELS = {
  smsApiKey:     { label: 'API Key / Account SID', placeholder: 'Enter your API key', type: 'text' },
  smsAuthToken:  { label: 'Auth Token', placeholder: 'Enter auth token', type: 'password' },
  smsSenderId:   { label: 'Sender ID', placeholder: 'e.g. SCHOOL', type: 'text' },
  smsFromNumber: { label: 'From Number', placeholder: '+1234567890', type: 'text' },
};

const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

const StatusBadge = ({ configured }) => (
  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
    {configured ? '✓ Configured' : 'Not configured'}
  </span>
);

const SmsConfig = () => {
  const [form, setForm] = useState({ smsProvider: '', smsApiKey: '', smsAuthToken: '', smsSenderId: '', smsFromNumber: '' });
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [msg, setMsg] = useState({ type: '', text: '' });

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  useEffect(() => {
    axios.get(`${API_BASE}/settings/sms-config`, { headers: headers() })
      .then(res => { setForm(prev => ({ ...prev, ...res.data })); setConfigured(res.data.configured); })
      .catch(() => flash('error', 'Failed to load SMS configuration'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.smsProvider) return flash('error', 'Please select an SMS provider');
    if (!form.smsApiKey || form.smsApiKey.startsWith('••')) return flash('error', 'API Key is required');
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/settings/sms-config`, form, { headers: headers() });
      flash('success', 'SMS configuration saved successfully');
      setConfigured(true);
    } catch (err) {
      flash('error', err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!testPhone) return flash('error', 'Enter a phone number to test');
    setTesting(true);
    try {
      const res = await axios.post(`${API_BASE}/settings/test-sms`, { phone: testPhone }, { headers: headers() });
      flash('success', res.data.message);
    } catch (err) {
      flash('error', err.response?.data?.error || 'Test SMS failed');
    } finally { setTesting(false); }
  };

  const visibleFields = PROVIDER_FIELDS[form.smsProvider] || [];

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">SMS Integration</h1>
              <p className="text-xs text-gray-400 mt-0.5">Configure your SMS gateway for notifications and alerts</p>
            </div>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="flex-1 overflow-auto p-6 space-y-6">
          {msg.text && (
            <div className={`px-4 py-3 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
              {msg.text}
            </div>
          )}

          {/* Status Card */}
          <div className="bg-white rounded-lg shadow p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${configured ? 'bg-green-100' : 'bg-gray-100'}`}>
              📱
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="font-semibold text-gray-800">SMS Gateway Status</h2>
                <StatusBadge configured={configured} />
              </div>
              <p className="text-sm text-gray-500 mt-0.5">
                {configured ? `Active provider: ${PROVIDERS.find(p => p.value === form.smsProvider)?.label || form.smsProvider}` : 'No SMS provider configured yet'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Config Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold text-gray-800 mb-5 flex items-center gap-2">
                  <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded flex items-center justify-center text-xs font-bold">1</span>
                  Provider Configuration
                </h3>
                <form onSubmit={handleSave} className="space-y-4">
                  {/* Provider Select */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SMS Provider *</label>
                    <select
                      value={form.smsProvider}
                      onChange={e => setForm(f => ({ ...f, smsProvider: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    {form.smsProvider && (
                      <p className="text-xs text-gray-400 mt-1">{PROVIDERS.find(p => p.value === form.smsProvider)?.desc}</p>
                    )}
                  </div>

                  {/* Dynamic Fields */}
                  {visibleFields.map(field => {
                    const { label, placeholder, type } = FIELD_LABELS[field];
                    const isSecret = type === 'password';
                    const show = showSecrets[field];
                    return (
                      <div key={field}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{label} *</label>
                        <div className="relative">
                          <input
                            type={isSecret && !show ? 'password' : 'text'}
                            value={form[field] || ''}
                            onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                            placeholder={placeholder}
                            className="w-full border rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          {isSecret && (
                            <button type="button" onClick={() => setShowSecrets(s => ({ ...s, [field]: !s[field] }))}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs">
                              {show ? 'Hide' : 'Show'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {form.smsProvider && (
                    <div className="pt-2">
                      <button type="submit" disabled={saving}
                        className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
                        {saving ? 'Saving...' : 'Save Configuration'}
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Provider Info */}
            <div className="space-y-4">
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded flex items-center justify-center text-xs font-bold">2</span>
                  Test SMS
                </h3>
                <p className="text-xs text-gray-500 mb-3">Send a test SMS to verify your configuration is working correctly.</p>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={13}
                  value={testPhone}
                  onChange={e => setTestPhone(e.target.value.replace(/[^\d+]/g, ''))}
                  placeholder="+919876543210"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-3"
                />
                <button
                  onClick={handleTest}
                  disabled={testing || !configured}
                  className="w-full bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium"
                >
                  {testing ? 'Sending...' : '📤 Send Test SMS'}
                </button>
                {!configured && <p className="text-xs text-gray-400 mt-2 text-center">Save your configuration first</p>}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">Setup Guide</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  {form.smsProvider === 'msg91' && <>
                    <li>1. Sign up at <strong>msg91.com</strong></li>
                    <li>2. Create a Flow or get your Auth Key</li>
                    <li>3. Register your Sender ID (DLTID)</li>
                    <li>4. Enter API Key + Sender ID above</li>
                  </>}
                  {form.smsProvider === 'fast2sms' && <>
                    <li>1. Register at <strong>fast2sms.com</strong></li>
                    <li>2. Go to Dev API section</li>
                    <li>3. Copy your API key</li>
                    <li>4. Enter API Key above</li>
                  </>}
                  {form.smsProvider === 'twilio' && <>
                    <li>1. Create account at <strong>twilio.com</strong></li>
                    <li>2. Get Account SID + Auth Token from Console</li>
                    <li>3. Buy or use a Twilio phone number</li>
                    <li>4. Enter all 3 fields above</li>
                  </>}
                  {form.smsProvider === 'textlocal' && <>
                    <li>1. Sign up at <strong>textlocal.in</strong></li>
                    <li>2. Go to Settings → API Keys</li>
                    <li>3. Generate API key</li>
                    <li>4. Enter API Key + Sender Name above</li>
                  </>}
                  {!form.smsProvider && <li>Select a provider to see setup instructions.</li>}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsConfig;
